/**
 * Reusable video embedding for YouTube, Vimeo and Wistia links, and direct files.
 * Extracted from coach-profile/hero.tsx so the offer page hero can reuse it.
 * Aliases: video player, vsl, watch intro, youtube embed, vimeo embed, wistia embed, inline play.
 */
import { useState, type ComponentPropsWithRef, type ReactNode } from "react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { cn } from "~/lib/cn";
import {
  isYoutubePlaceholderThumbnail,
  toEmbedUrl,
  withAutoPlay,
} from "~/lib/shared/video-url";

/** Opens a coach's intro video in a dialog from a trigger. */
export function WatchIntroDialog({
  videoUrl,
  fullName,
  trigger,
}: {
  videoUrl: string;
  fullName: string;
  /** Optional custom trigger; defaults to the "Watch intro →" text link. */
  trigger?: ReactNode;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            className="text-sm font-medium text-gray-900 underline decoration-gray-300 underline-offset-4 hover:decoration-gray-700"
          >
            Watch intro →
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogTitle className="sr-only">{`Intro video — ${fullName}`}</DialogTitle>
        <div className="aspect-video w-full overflow-hidden rounded-lg bg-gray-900">
          <VideoEmbed url={videoUrl} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Embeds a video from its URL, with optional autoplay. */
export function VideoEmbed({
  url,
  autoPlay = false,
  title = "Coach intro",
}: {
  url: string;
  /** Start playback immediately. Safe only when mounted in response to a user
   * gesture (e.g. clicking a poster) — browsers block unsolicited autoplay. */
  autoPlay?: boolean;
  /** Accessible title for the iframe so screen readers announce which video is
   * playing. Defaults to a generic label for the dialog usage. */
  title?: string;
}) {
  const embed = toEmbedUrl(url);
  if (embed) {
    const src = autoPlay ? withAutoPlay(embed) : embed;
    return (
      <iframe
        title={title}
        src={src}
        className="size-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }
  // Fallback: direct video file. Empty <track> satisfies the a11y
  // "media has captions" lint rule — coach videos don't ship captions today.
  return (
    <video
      src={url}
      controls
      playsInline
      autoPlay={autoPlay}
      className="size-full"
    >
      <track kind="captions" />
    </video>
  );
}

/**
 * Inline click-to-play video. Shows a poster (the `candidates` are tried in
 * order, stepping down on each `<img>` load error) with a play overlay; a single
 * click swaps the poster in place for an autoplaying embed — the video plays on
 * the page itself, with no dialog and no second click on the YouTube player.
 *
 * Use this instead of `WatchIntroDialog` when the video should be the focal,
 * on-page element rather than a modal pop-up.
 */
export function InlineVideoEmbed({
  url,
  posterCandidates,
  label = "Play the video",
  className,
}: {
  url: string;
  /** Poster image URLs, tried in order; falls back to a gradient if all fail. */
  posterCandidates: string[];
  label?: string;
  className?: string;
}) {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <div
        className={cn(
          "aspect-video w-full overflow-hidden rounded-2xl bg-gray-900 shadow-xl ring-1 shadow-purple-900/10 ring-gray-900/5",
          className,
        )}
      >
        <VideoEmbed url={url} autoPlay title={label} />
      </div>
    );
  }

  return (
    <VideoPosterButton
      candidates={posterCandidates}
      aria-label={label}
      className={className}
      onClick={() => setPlaying(true)}
    />
  );
}

/**
 * A video's still image. Tries each candidate URL in order and steps down to
 * the next on a load error or when YouTube sends its grey "no image"
 * placeholder (which loads without an error). Renders `fallback` once every
 * candidate has failed, or when there are none. A new candidate list starts
 * again from its first image.
 */
export function VideoPosterImage({
  candidates,
  className,
  fallback = null,
}: {
  candidates: readonly string[];
  className?: string;
  fallback?: ReactNode;
}) {
  const listKey = candidates.join("\n");
  const [failed, setFailed] = useState({ listKey, count: 0 });
  const index = failed.listKey === listKey ? failed.count : 0;
  const src = candidates[index];
  const stepDown = () => setFailed({ listKey, count: index + 1 });

  if (!src) return fallback;
  return (
    // Plain <img> rather than CDNImage because the YouTube candidates are
    // already absolute URLs and we need a stable onError chain.
    // oxlint-disable-next-line jsx-a11y/alt-text
    <img
      src={src}
      alt=""
      className={className}
      onError={stepDown}
      onLoad={(event) => {
        if (
          isYoutubePlaceholderThumbnail(src, event.currentTarget.naturalWidth)
        )
          stepDown();
      }}
    />
  );
}

/**
 * Click-to-play poster: a {@link VideoPosterImage} over a purple gradient,
 * which shows when every candidate fails (or none were provided).
 *
 * Forwards `...props`/`ref` onto the real `<button>` so it works both as a
 * standalone inline trigger (via `onClick`) and as a Radix `DialogTrigger
 * asChild` Slot child (which injects click/ref/ARIA props).
 */
export function VideoPosterButton({
  candidates,
  ref,
  className,
  ...props
}: { candidates: string[] } & ComponentPropsWithRef<"button">) {
  return (
    <button
      ref={ref}
      type="button"
      // Default name so the extracted button is never announced empty; any
      // `aria-label` in `props` (e.g. from InlineVideoEmbed) overrides it.
      aria-label="Play video"
      className={cn(
        "group relative aspect-video w-full overflow-hidden rounded-2xl bg-linear-to-br from-purple-700 via-purple-900 to-gray-900 shadow-xl ring-1 shadow-purple-900/10 ring-gray-900/5",
        className,
      )}
      {...props}
    >
      <VideoPosterImage
        candidates={candidates}
        className="absolute inset-0 size-full object-cover"
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-linear-to-t from-gray-900/50 via-transparent to-transparent"
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 grid place-items-center"
      >
        <span className="grid size-20 place-items-center rounded-full bg-white/95 text-3xl text-purple-700 shadow-2xl transition group-hover:scale-105">
          ▶
        </span>
      </span>
    </button>
  );
}
